import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Users, Award, Mail, MessageSquare, FileText, TrendingUp, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import api from '../../utils/api';

const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -4 }}
    transition={{ duration: 0.3 }}
  >
    <Card className="border-l-4 hover:shadow-lg transition-shadow" style={{ borderLeftColor: color }}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 font-medium">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
            {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
          </div>
          <div className="h-16 w-16 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
            <Icon className="h-8 w-8" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await api.get('/admin/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div
          className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    );
  }

  const statCards = [
    {
      icon: Calendar,
      title: 'Total Trainings',
      value: stats?.total_trainings || 0,
      subtitle: 'All time',
      color: '#3B82F6'
    },
    {
      icon: Users,
      title: 'Total Registrations',
      value: stats?.total_registrations || 0,
      subtitle: 'All participants',
      color: '#10B981'
    },
    {
      icon: Award,
      title: 'Certificates Issued',
      value: stats?.total_certificates || 0,
      subtitle: 'All time',
      color: '#F59E0B'
    },
    {
      icon: Mail,
      title: 'Pending Contacts',
      value: stats?.pending_contacts || 0,
      subtitle: 'Need response',
      color: '#EF4444'
    },
    {
      icon: MessageSquare,
      title: 'Active Testimonials',
      value: stats?.active_testimonials || 0,
      subtitle: 'Published',
      color: '#8B5CF6'
    },
    {
      icon: FileText,
      title: 'Published Blogs',
      value: stats?.published_blogs || 0,
      subtitle: 'Live posts',
      color: '#EC4899'
    },
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white shadow-xl"
      >
        <h1 className="text-3xl font-bold mb-2">Welcome to ISDE Academy Admin Panel</h1>
        <p className="text-blue-100 text-lg">Manage your training programs, registrations, and content efficiently</p>
      </motion.div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((card, index) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <StatCard {...card} />
          </motion.div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Upcoming Trainings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              Upcoming Trainings
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.upcoming_trainings?.length > 0 ? (
              <div className="space-y-4">
                {stats.upcoming_trainings.map((training) => (
                  <div key={training.id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Calendar className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 truncate">{training.title}</h4>
                      <p className="text-sm text-gray-600">
                        {new Date(training.date).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                      <div className="flex gap-2 mt-1">
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">{training.mode}</span>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">{training.seats_available} seats</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No upcoming trainings</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Registrations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Recent Registrations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_registrations?.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_registrations.map((reg) => (
                  <div key={reg.id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <Users className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 truncate">{reg.full_name}</h4>
                      <p className="text-sm text-gray-600 truncate">{reg.email}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(reg.registered_at).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short'
                        })}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded flex-shrink-0 ${
                      reg.registration_status === 'confirmed' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {reg.registration_status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No recent registrations</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
